import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { MdClose, MdMic, MdMicOff, MdSearch } from 'react-icons/md';
import './VoiceSearchModal.css';

export default function VoiceSearchModal({ isOpen, onClose, onSearch }) {
  const [isListening, setIsListening] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [statusText, setStatusText] = useState('Listening... Say something or hum a tune');
  const recognitionRef = useRef(null);
  const transcriptRef = useRef('');

  const handleClose = () => {
    if (isClosing) return;
    setIsClosing(true);
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) {}
    }
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 240);
  };

  const startListening = async () => {
    setIsListening(true);
    setStatusText('Listening... Say something or hum a tune');
    setTranscript('');
    transcriptRef.current = '';

    // Request browser microphone permission directly
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (err) {
        console.warn('Microphone permission request error:', err);
      }
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (SpeechRecognition) {
      try {
        if (recognitionRef.current) {
          try { recognitionRef.current.stop(); } catch (e) {}
        }

        const recognition = new SpeechRecognition();
        recognition.continuous = false;
        recognition.interimResults = true;
        recognition.lang = 'en-US';

        recognition.onstart = () => {
          setIsListening(true);
          setStatusText('Listening... Say something or hum a tune');
        };

        recognition.onresult = (event) => {
          let currentTranscript = '';
          for (let i = event.resultIndex; i < event.results.length; i++) {
            currentTranscript += event.results[i][0].transcript;
          }
          const text = currentTranscript.trim();
          transcriptRef.current = text;
          setTranscript(text);
          if (text) {
            setStatusText(`"${text}"`);
          }
        };

        recognition.onend = () => {
          const finalSpoken = transcriptRef.current;
          if (finalSpoken) {
            setIsListening(false);
            setStatusText(`Searching for: "${finalSpoken}"`);
            setTimeout(() => {
              onSearch(finalSpoken);
              handleClose();
            }, 800);
          } else {
            setIsListening(false);
            setStatusText('Did not catch that. Tap mic to try again.');
          }
        };

        recognition.onerror = (event) => {
          console.warn('Speech recognition error:', event.error);
          if (event.error === 'not-allowed') {
            setIsListening(false);
            setStatusText('Microphone access blocked. Allow access in browser bar.');
          } else {
            // Keep listening active
            setIsListening(true);
            setStatusText('Listening... Hum a tune or say a song title');
          }
        };

        recognitionRef.current = recognition;
        recognition.start();
      } catch (err) {
        console.error('Speech recognition exception:', err);
        setIsListening(true);
        setStatusText('Listening... Say something or hum a tune');
      }
    } else {
      setIsListening(true);
      setStatusText('Listening... Say a song name, lyrics, or hum');
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
      startListening();
    } else {
      setIsListening(false);
      setIsClosing(false);
      setTranscript('');
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (e) {}
      }
    }

    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (e) {}
      }
    };
  }, [isOpen]);

  const toggleListening = () => {
    if (isListening) {
      setIsListening(false);
      setStatusText('Microphone paused. Tap mic to start listening.');
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (e) {}
      }
    } else {
      startListening();
    }
  };

  const handleManualSubmit = () => {
    const text = (transcriptRef.current || transcript || '').trim();
    if (text) {
      onSearch(text);
      handleClose();
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className={`yt-voice-modal-backdrop ${isClosing ? 'exiting' : ''}`} onClick={handleClose}>
      <div className={`yt-voice-modal-card ${isClosing ? 'exiting' : ''}`} onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="yt-voice-close-btn" onClick={handleClose} title="Close">
          <MdClose size={24} />
        </button>

        {/* Modal Title */}
        <h2 className="yt-voice-title">
          {transcript ? 'Recognizing...' : 'Search with your voice'}
        </h2>
        <p className="yt-voice-subtitle">{statusText}</p>

        {/* Creative Doodle Container */}
        <div className="yt-voice-doodle-container">
          {/* Clean Animated Floating Music Notes */}
          <div className={`yt-doodle-particles ${isListening ? 'active' : ''}`}>
            <span className="yt-note n1">♪</span>
            <span className="yt-note n2">♫</span>
            <span className="yt-note n3">♬</span>
            <span className="yt-note n4">♩</span>
          </div>

          {/* Central Morphing Microphone / Emoji Off Button */}
          <div className="yt-voice-mic-wrapper">
            <button
              type="button"
              className={`yt-voice-mic-btn ${isListening ? 'listening' : 'off'}`}
              onClick={toggleListening}
              title={isListening ? 'Click to pause listening' : 'Click to start listening'}
            >
              {/* Active Mic State */}
              <div className={`yt-mic-active-wrapper ${isListening ? 'visible' : 'hidden'}`}>
                <div className="yt-mic-pulse-rings">
                  <div className="ring r1" />
                  <div className="ring r2" />
                  <div className="ring r3" />
                </div>
                <MdMic size={36} className="yt-mic-icon" />
              </div>

              {/* Muted Mic Off State: Vector Emoji Face with X X Eyes & Mouth Shut */}
              <div className={`yt-mic-emoji-off-wrapper ${!isListening ? 'visible' : 'hidden'}`}>
                <svg
                  className="yt-mic-emoji-off-svg"
                  viewBox="0 0 64 64"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Face Circle outline */}
                  <circle cx="32" cy="32" r="27" stroke="currentColor" strokeWidth="3" fill="none" />

                  {/* Left Eye: 'X' */}
                  <path d="M 22 21 L 28 27" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
                  <path d="M 28 21 L 22 27" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />

                  {/* Right Eye: 'X' */}
                  <path d="M 36 21 L 42 27" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
                  <path d="M 43 21 L 37 27" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />

                  {/* Mouth Shut Line */}
                  <line x1="22" y1="42" x2="42" y2="42" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
                </svg>
              </div>
            </button>
          </div>
        </div>

        {/* Live Recognized Transcript Display & Manual Search Action */}
        {transcript && (
          <div className="yt-voice-transcript-box">
            <span className="yt-transcript-text">{transcript}</span>
            <button
              className="yt-transcript-search-btn"
              onClick={handleManualSubmit}
              title="Search this text"
            >
              <MdSearch size={20} />
              <span>Search</span>
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
